import cn from 'classnames';
import React, { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { useWidgetMaterial } from '@pages/widget/order/hooks';
import { modelsActions, selectRelatedModelsDataById } from '@modules/models';
import { selectCorrectObjectModelId, selectCurrentProduct, selectHasDefinedModel } from '@modules/quotation';
import { ObjectModelModel } from '@models';
import { ViewerErrorBoundary } from './viewer-error-boundary';
import { DxfViewer } from './dxf-viewer';
import { ModelViewer } from './model-viewer';
import { NonCadViewer } from './non-cad-viewer';
import { ProductViewer } from './product-viewer';

import styles from './viewer-handler.module.scss';

export interface ProcessableModelViewerHandlerProps {
    model: ObjectModelModel;
    isModelFileLoaded: boolean;
}

export const ProcessableModelViewerHandler = ({ model, isModelFileLoaded }: ProcessableModelViewerHandlerProps) => {
    const dispatch = useAppDispatch();
    const { widgetMaterial } = useWidgetMaterial();
    const correctObjectModelId = useAppSelector(selectCorrectObjectModelId);

    const setFileModelLoaded = useCallback(
        (modelFileUrl: string) => {
            if (isModelFileLoaded) {
                return;
            }

            dispatch(
                modelsActions.updateObjectModelRelatedData({
                    model_id: model.id,
                    data: { isModelFileLoaded: true },
                }),
            );
        },
        [dispatch, model.id, isModelFileLoaded],
    );

    return model.isDxf ? (
        <DxfViewer model={model} onLoadFileCallback={setFileModelLoaded} />
    ) : (
        <ModelViewer
            model={model}
            correctObjectModelId={correctObjectModelId}
            material={widgetMaterial}
            onLoadFileCallback={setFileModelLoaded}
        />
    );
};

export interface ViewerHandlerProps {
    model?: ObjectModelModel;
}

export const ViewerHandler: React.FC<ViewerHandlerProps> = ({ model }) => {
    // todo isModelFileLoaded move to viewer lifecycle (events?)
    const isModelFileLoaded = useAppSelector(state => {
        return model?.is_processable ? Boolean(selectRelatedModelsDataById(state, model.id)?.isModelFileLoaded) : true;
    });
    const hasDefinedModel = useAppSelector(selectHasDefinedModel);
    const currentProduct = useAppSelector(selectCurrentProduct);

    const hasModel = model;
    const shouldRenderProductViewer = !hasDefinedModel && !!currentProduct;
    const noModelViewer = shouldRenderProductViewer ? <ProductViewer product={currentProduct} /> : null;

    return (
        <div
            className={cn([
                styles.box,
                { [styles.empty]: !hasModel && !noModelViewer, [styles.loading]: !isModelFileLoaded },
            ])}
        >
            <ViewerErrorBoundary modelId={model?.id}>
                {hasModel ? (
                    model.is_processable ? (
                        <ProcessableModelViewerHandler model={model} isModelFileLoaded={isModelFileLoaded} />
                    ) : (
                        <NonCadViewer model={model} />
                    )
                ) : (
                    noModelViewer
                )}
            </ViewerErrorBoundary>
        </div>
    );
};
